import { _GettersTree, acceptHMRUpdate, defineStore } from 'pinia'
import $ from 'jquery'
import Stripe from 'stripe'
import { differenceInCalendarDays, format, startOfDay } from 'date-fns'
import { toZonedTime } from 'date-fns-tz'
import {
  GetSubscriptionResponse,
  SubscriptionV2State,
  GetPlansV2Response,
  StripePaymentMethod,
  type Product,
  type StripePlan,
  GetPlansQuery,
  PlansEnum,
  PlanV1
} from '../types/subscription.type'
import { StripeSubscriptionStatus, ToastTypes } from '../enums/components.enum'
import { useUserStore } from './user'
import { useFeatureFlagsStore } from './feature-flags'
import CustomError from '../js/custom-error'
import { type ToastResponse } from '@/components/Tailwind/Toast.vue'

export type DowngradeHubspotForm = {
  cancelReasons: string
  cancelReasonText: string
}

export type PlanPricing = {
  year: { offerPrice: number; amount: number; hasDiscountedPrice: boolean; discountName?: string }
  month: { offerPrice: number; amount: number; hasDiscountedPrice: boolean; discountName?: string }
}

export function getEmptyPricing(): PlanPricing {
  return {
    year: { offerPrice: 0, amount: 0, hasDiscountedPrice: false },
    month: { offerPrice: 0, amount: 0, hasDiscountedPrice: false }
  }
}

export function getAnnualSavings(
  annualAmount: number | null | undefined,
  monthlyAmount: number | null | undefined
): number {
  if (!annualAmount || !monthlyAmount) return 0
  return (monthlyAmount * 12 - annualAmount) / 100
}

export interface SubscriptionStoreState {
  subscription?: GetSubscriptionResponse
  products?: GetPlansV2Response
  clientSecret?: any
  subscriptionRequest?: any
  currentPlan?: Stripe.Plan | PlanV1
  stripePaymentMethod?: StripePaymentMethod
  stripeSubscription?: (Stripe.Subscription & { currentTimeOverride?: number }) | null
  stripeSubscriptionSchedule?: Stripe.SubscriptionSchedule | null
  loading: {
    subscription: boolean
    products: boolean
    payment: boolean
  }
}

type SubscriptionStoreGetters = {
  currentMonthlyCharge(state: SubscriptionStoreState): number
  isV2Customer(state: SubscriptionStoreState): boolean
  isSubscriptionActive(state: SubscriptionStoreState): boolean
  isInTrial(state: SubscriptionStoreState): boolean
  trialDaysLeft(state: SubscriptionStoreState): number
  hasPaymentMethod(state: SubscriptionStoreState): boolean
  productPricing(state: SubscriptionStoreState): (productId: number) => PlanPricing
  annualSavings(state: SubscriptionStoreState): Record<string, number> | undefined
  trialStartDate(state: SubscriptionStoreState): Date | undefined
  trialEndDate(state: SubscriptionStoreState): Date | undefined
  showV2TrialMessaging(state: SubscriptionStoreState): boolean
  findPlanByStripeId(
    state: SubscriptionStoreState
  ): (stripePlanId: string) => StripePlan | undefined
  findProductByStripePlanId(
    state: SubscriptionStoreState
  ): (stripePlanId: string) => Product | undefined
  currentTime(state: SubscriptionStoreState): number
  scheduledSubscriptionChange(
    state: SubscriptionStoreState
  ): { planName: string; scheduledDate: string } | undefined
  nextBillingDate(state: SubscriptionStoreState): number | null
  nextBillingAmount(state: SubscriptionStoreState): number | null
  appliedCouponName(state: SubscriptionStoreState): string | null
}

type SubscriptionStoreActions = {
  getSubscriptionPlans(venueCount?: number): Promise<GetPlansV2Response>
  getSubscription(): Promise<ToastResponse>
  getSubscriptionSecret(): Promise<void>
  submitSubscriptionRequest(stripePlanId: string, paymentMethodId: string): Promise<void>
  cancelScheduledSubscriptionChange(): Promise<void>
  updatePaymentMethod(setupIntentId: string): Promise<void>
  downgradeToBasic(): Promise<ToastResponse>
  sendHubspotDowngrade(data: DowngradeHubspotForm): Promise<void>
}

export const useSubscriptionsStore = defineStore<
  'subscriptions',
  SubscriptionStoreState,
  SubscriptionStoreGetters,
  SubscriptionStoreActions
>('subscriptions', {
  state: (): SubscriptionStoreState => {
    return {
      subscription: undefined,
      loading: {
        subscription: false,
        products: false,
        payment: false
      }
    }
  },
  getters: {
    nextBillingDate(state): number | null {
      if (!state.stripeSubscription || state.stripeSubscription.status === 'canceled') {
        return null
      }
      const nextPhase = state.stripeSubscriptionSchedule?.phases.find(
        (phase) => phase.start_date * 1000 > this.currentTime
      )
      if (nextPhase) {
        return nextPhase.start_date * 1000
      }
      if (state.stripeSubscription.current_period_end) {
        return state.stripeSubscription.current_period_end
      }

      return null
    },
    nextBillingAmount(state): number | null {
      if (!state.stripeSubscription || state.stripeSubscription.status === 'canceled') {
        return null
      }
      const nextPhase = state.stripeSubscriptionSchedule?.phases.find(
        (phase) => phase.start_date * 1000 > this.currentTime
      )

      let subtotalInCents = 0
      let discount: Stripe.Discount | null | undefined = null

      if (nextPhase) {
        subtotalInCents = nextPhase.items.reduce((total, item) => {
          const price = item.price as Stripe.Price
          return total + (price.unit_amount || 0) * (item.quantity || 1)
        }, 0)
        discount = nextPhase.coupon
          ? ({ coupon: nextPhase.coupon as Stripe.Coupon } as Stripe.Discount)
          : null
      } else {
        subtotalInCents = state.stripeSubscription.items.data.reduce((total, item) => {
          return total + (item.price.unit_amount || 0) * (item.quantity || 1)
        }, 0)
        discount = state.stripeSubscription.discount
      }
      if (discount && discount.coupon) {
        if (discount.coupon.amount_off) {
          subtotalInCents = Math.max(0, subtotalInCents - discount.coupon.amount_off)
        } else if (discount.coupon.percent_off) {
          subtotalInCents = Math.round(subtotalInCents * (1 - discount.coupon.percent_off / 100))
        }
      }
      return subtotalInCents / 100
    },
    appliedCouponName(state): string | null {
      if (!state.stripeSubscription || state.stripeSubscription.status === 'canceled') {
        return null
      }
      const nextPhase = state.stripeSubscriptionSchedule?.phases.find(
        (phase) => phase.start_date * 1000 > this.currentTime
      )

      if (nextPhase?.coupon) {
        const coupon = nextPhase.coupon as Stripe.Coupon
        return coupon.name || null
      }
      if (state.stripeSubscription.discount?.coupon) {
        return state.stripeSubscription.discount.coupon.name || null
      }
      return null
    },
    currentMonthlyCharge(state): number {
      const subscriptionItem = state.subscription?.stripe_subscription?.items.data[0]
      if (!subscriptionItem) return 0

      const amount = subscriptionItem.price.unit_amount ?? 0
      const interval = subscriptionItem.price.recurring?.interval

      if (interval === 'year') {
        return Math.round(amount / 12) // Monthly equivalent
      }

      return amount // Monthly plan as-is
    },
    isInTrial(state): boolean {
      const stripeStatus = state.subscription?.stripe_subscription?.status
      const subv2State = state.subscription?.subv2_data?.state
      return (
        subv2State === SubscriptionV2State.Trial ||
        stripeStatus === StripeSubscriptionStatus.Trialing
      )
    },
    isV2Customer(state): boolean {
      return !!state?.subscription?.subv2_data
    },
    isSubscriptionActive(state): boolean {
      const userStore = useUserStore()
      return (
        state?.stripeSubscription?.status !== 'canceled' && userStore?.currentOrganization?.plan > 0
      )
    },
    showV2TrialMessaging(state): boolean {
      // to show V2 Trial messaging, the feature flag must be on and the user must be in a trial
      const userStore = useUserStore()
      const { featureFlags } = useFeatureFlagsStore()
      if (!featureFlags.subscriptionV2) return false
      return (
        !!state.subscription?.subv2_data &&
        this.isInTrial &&
        userStore?.currentOrganization?.plan > 0 &&
        userStore.isAdmin
      )
    },
    trialDaysLeft(state): number {
      const trialEnd = state.subscription?.subv2_data?.trial_end_at
      if (!trialEnd) return -1

      const userStore = useUserStore()
      const timezone = userStore?.currentVenue?.time_zone ?? 'America/New_York'

      const endOfTrialDay = startOfDay(toZonedTime(trialEnd, timezone))
      const todayInZone = startOfDay(toZonedTime(this.currentTime, timezone))

      return differenceInCalendarDays(endOfTrialDay, todayInZone)
    },
    hasPaymentMethod(state): boolean {
      return !!state.stripePaymentMethod
    },
    productPricing: (state) => (productId: number) => {
      const product = state?.products?.[productId]
      if (!product || !state?.subscription?.subv2_data) {
        return getEmptyPricing()
      }

      const annualPlan = product.plans.find((plan) => plan.interval === 'year')
      const monthlyPlan = product.plans.find((plan) => plan.interval === 'month')

      const result = {
        year: {
          offerPrice: annualPlan?.discountedAmount ?? annualPlan?.baseAmount ?? 0,
          amount: annualPlan?.baseAmount ?? 0,
          hasDiscountedPrice: annualPlan?.isDiscounted ?? false,
          discountName: annualPlan?.discountName
        },
        month: {
          offerPrice: monthlyPlan?.discountedAmount ?? monthlyPlan?.baseAmount ?? 0,
          amount: monthlyPlan?.baseAmount ?? 0,
          hasDiscountedPrice: monthlyPlan?.isDiscounted ?? false,
          discountName: monthlyPlan?.discountName
        }
      }

      return result
    },
    annualSavings: (state) => {
      const savingsMap: Record<string, number> = {}
      if (!state.products) return savingsMap

      for (const product of state.products) {
        const pricing = {
          year: { amount: 0 },
          month: { amount: 0 }
        }

        for (const plan of product.plans ?? []) {
          if (plan.interval === 'year') {
            pricing.year.amount = plan.amount || 0
          }
          if (plan.interval === 'month') {
            pricing.month.amount = plan.amount || 0
          }
        }
        const savings = getAnnualSavings(pricing.year.amount, pricing.month.amount)
        savingsMap[product.stripe_product_id] = savings
      }
      return savingsMap
    },
    trialStartDate(state): Date {
      const userStore = useUserStore()
      const timezone = userStore?.currentVenue?.time_zone ?? 'America/New_York'

      const trialStart = state.subscription?.subv2_data?.trial_start_at
      if (!trialStart) return new Date()

      return startOfDay(toZonedTime(trialStart, timezone))
    },
    trialEndDate(state): Date {
      const userStore = useUserStore()
      const timezone = userStore?.currentVenue?.time_zone ?? 'America/New_York'

      const trialEnd = state.subscription?.subv2_data?.trial_end_at
      if (!trialEnd) return new Date()

      return startOfDay(toZonedTime(trialEnd, timezone))
    },
    findPlanByStripeId(state) {
      return (planId: string): StripePlan | undefined => {
        if (!planId) return undefined
        for (const product of state.products || []) {
          if (product.plans && Array.isArray(product.plans)) {
            const foundPlan = product.plans.find((p) => p.id === planId)
            if (foundPlan) {
              return foundPlan
            }
          }
        }
        return undefined
      }
    },
    findProductByStripePlanId(state) {
      return (stripePlanId: string): Product | undefined => {
        if (!stripePlanId) return undefined

        const findIn = (products?: Product[]) =>
          products?.find((product) => product.plans?.some((plan) => plan.id === stripePlanId))

        return findIn(state.products)
      }
    },
    currentTime(state) {
      if (state.stripeSubscription?.currentTimeOverride) {
        return state.stripeSubscription.currentTimeOverride
      }

      return Math.floor(new Date().getTime())
    },
    scheduledSubscriptionChange(state) {
      const futureScheduledChanges =
        state?.stripeSubscriptionSchedule?.phases.filter(
          (phase) => phase.start_date >= this.currentTime / 1000
        ) || []

      if (futureScheduledChanges.length > 0) {
        const lastScheduledChange = futureScheduledChanges[futureScheduledChanges.length - 1]
        return {
          planName: lastScheduledChange?.items[0].price?.nickname || 'Basic',
          scheduledDate: format(lastScheduledChange?.start_date * 1000, 'MMMM d, yyyy')
        }
      }
      return undefined
    }
  },
  actions: {
    async getSubscriptionPlans(venueCount?: number) {
      const baseQuery: GetPlansQuery = {
        customerId: this.stripeSubscription?.customer?.id || this.stripeSubscription?.customer
      }
      if (this.stripeSubscription?.id) {
        baseQuery.subscriptionId = this.stripeSubscription.id
      }
      if (venueCount) {
        baseQuery.venueCount = venueCount
      }
      const products = (await $.get('/plans/v2', baseQuery)) as GetPlansV2Response
      this.products = products
      return products
    },
    async getSubscription() {
      this.loading.subscription = true
      try {
        const subscriptionRequestData = (await $.get(
          '/subscriptions/request'
        )) as GetSubscriptionResponse
        this.subscription = subscriptionRequestData
        this.stripeSubscription = subscriptionRequestData.stripe_subscription
        this.stripeSubscriptionSchedule = subscriptionRequestData.stripe_schedule
        const isCancelled = subscriptionRequestData.stripe_subscription?.status === 'canceled'
        this.currentPlan = !isCancelled
          ? subscriptionRequestData?.stripe_subscription?.items?.data?.[0]?.plan ||
            subscriptionRequestData.plan
          : undefined
        this.stripePaymentMethod =
          subscriptionRequestData?.subv2_data?.payment_method ||
          subscriptionRequestData?.stripe_payment_method
        this.subscriptionRequest = subscriptionRequestData?.request || null
        return { success: true }
      } catch (e) {
        this.subscription = {} as GetSubscriptionResponse
        this.stripeSubscription = null
        return {
          success: false,
          toast: {
            severity: ToastTypes.Error,
            summary: 'There was an error retrieving your subscription data.',
            detail: 'Please try again later.',
            group: 'tw',
            life: 3000
          }
        }
      } finally {
        this.loading.subscription = false
      }
    },
    async getSubscriptionSecret() {
      const paymentSecret = await $.get('/subscriptions/payment_secret')
      this.clientSecret = paymentSecret.client_secret
    },
    async submitSubscriptionRequest(stripePlanId: string, paymentMethodId: string) {
      const subscriptionRequestData = await $.ajax({
        url: '/subscriptions',
        method: 'PUT',
        data: {
          stripe_plan_id: stripePlanId,
          payment_method_id: paymentMethodId
        }
      })
      this.subscriptionRequest = subscriptionRequestData.subscription_request
    },
    async cancelScheduledSubscriptionChange() {
      const res = await $.ajax({
        url: `/subscriptions/schedule/${this.stripeSubscriptionSchedule?.id}/cancel`,
        method: 'DELETE'
      })
      if (res.success) {
        await this.getSubscription()
      }
    },
    async updatePaymentMethod(setupIntentId: string) {
      try {
        const res = await $.ajax({
          url: `/subscriptions/update_default_payment`,
          method: 'PUT',
          data: {
            setup_intent: setupIntentId
          }
        })
        if (res.success && res.stripe_payment_method) {
          await this.getSubscription()
          this.stripePaymentMethod = res.stripe_payment_method
        }
      } catch (e) {
        console.error('Failed to update payment method:', e)
      }
    },
    async downgradeToBasic() {
      try {
        const basicPlanId = this.products?.find((p) => p.id === PlansEnum.Basic)?.plans?.[0]?.id
        const paymentMethodId = this.stripePaymentMethod?.id

        if (!basicPlanId || !paymentMethodId) {
          throw new Error('Required data for downgrade is missing.')
        }

        await this.submitSubscriptionRequest(basicPlanId, paymentMethodId)
        return {
          success: true,
          toast: {
            severity: ToastTypes.Success,
            summary: 'Success',
            detail: 'Your account is scheduled to be downgraded.',
            group: 'tw',
            life: 3000
          }
        }
      } catch (error) {
        throw new CustomError('failed_downgrade', error as Error, {
          severity: ToastTypes.Error,
          summary: 'Error',
          detail: 'Failed to downgrade your plan. Please try again.',
          group: 'tw',
          life: 3000
        })
      }
    },
    async sendHubspotDowngrade(data: DowngradeHubspotForm) {
      try {
        await $.ajax({
          url: `/hubspot/cancel_reason`,
          method: 'POST',
          data
        })
      } catch (error) {
        console.error('Failed to send Hubspot downgrade reason:', error)
      }
    }
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useSubscriptionsStore, import.meta.hot))
}
